import {Box, Paper, Stack} from "@mui/material";
import {PANEL_GUTTER, PANEL_SLIDE_TRANSITION} from "./useNavLayout";
import {useSlideGesture} from "./useSlideGesture";
import {ReactNode} from "react";

export interface NavigationSheetProps {
    headerHeight: number;
    contentHeight: number;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    /** Grabber and switcher. Stays on screen when the sheet is closed and is the drag handle. */
    header: ReactNode;
    children: ReactNode;
}

/**
 * The phone portrait navigation: slides up over the panorama and can be dragged back down by its header.
 */
export function NavigationSheet(props: NavigationSheetProps) {
    const {headerHeight, contentHeight, isOpen, setIsOpen, header, children} = props;

    // Closed leaves only the header showing; open pulls the whole sheet into view.
    const {offset, isDragging, handlers} = useSlideGesture({
        axis: "y",
        closedOffset: contentHeight,
        isOpen,
        setIsOpen,
        onTap: ({startedOpen, fromControl}) => {
            // Tapping a switcher button should reveal the section it selects and never hide it.
            if (fromControl) {
                if (!startedOpen) setIsOpen(true);
            } else {
                setIsOpen(!startedOpen);
            }
        }
    });

    return <Paper elevation={8}
                  sx={{
                      pointerEvents: "auto",
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: headerHeight + contentHeight,
                      display: "flex",
                      flexDirection: "column",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      transform: `translateY(${offset}px)`,
                      // While dragging, follow the pointer exactly; animate only when settling.
                      transition: isDragging ? "none" : `transform ${PANEL_SLIDE_TRANSITION}`
                  }}>

        <Stack alignItems={"center"} justifyContent={"center"} spacing={1}
               {...handlers}
               sx={{
                   flexShrink: 0,
                   height: headerHeight,
                   // Inset on the container, not on the children: the container has no explicit width,
                   // so padding narrows the children instead of overflowing the sheet.
                   px: PANEL_GUTTER,
                   cursor: "grab",
                   // Stops the browser claiming the vertical drag for scrolling or overscroll.
                   touchAction: "none"
               }}>
            {header}
        </Stack>

        <Box sx={{flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden"}}>
            {children}
        </Box>
    </Paper>
}
